import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Settings, BarChart3 } from 'lucide-react';
import adminBookingService from '../../services/adminBookingService';
import { validateToken, handleTokenExpiration } from '../../utils/authUtils';
import './AdminDashboard.css';

// Debounce utility function
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

const AdminDashboard = () => {
  const [bookings, setBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [confirmingBooking, setConfirmingBooking] = useState(null);
  const [sortBy, setSortBy] = useState('date'); // 'date' | 'status'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' | 'desc'

  // Debounced search function
  const debouncedSearch = React.useCallback(
    debounce(async (searchTerm) => {
      if (!searchTerm.trim()) {
        // If search is empty, load all bookings
        await loadAllBookings();
        return;
      }

      try {
        setSearchLoading(true);
        setError(null);
        
        // Check authentication before searching
        if (!validateToken()) {
          throw new Error('Authentication failed. Please login again.');
        }
        
        console.log('Searching for:', searchTerm);
        const apiBookings = await adminBookingService.searchBookings(searchTerm);
        console.log('Search results:', apiBookings);
        
        const transformedBookings = apiBookings.map(booking => 
          adminBookingService.transformBookingData(booking)
        );
        setBookings(transformedBookings);
      } catch (err) {
        console.error('Error searching bookings:', err);
        if (err.message.includes('Access denied') || err.message.includes('403')) {
          setError('Access denied. Please check your authentication and try logging in again.');
        } else if (err.message.includes('No authentication token')) {
          setError('Authentication failed. Please login again.');
        } else {
          setError('Failed to search bookings. Please try again.');
        }
        setBookings([]);
      } finally {
        setSearchLoading(false);
      }
    }, 500),
    []
  );

  // Load all bookings function
  const loadAllBookings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Check if user is authenticated
      if (!validateToken()) {
        throw new Error('Authentication failed. Please login again.');
      }
      
      console.log('Loading bookings...');
      const apiBookings = await adminBookingService.fetchAllBookings();
      console.log('API bookings received:', apiBookings);
      
      const transformedBookings = apiBookings.map(booking => 
        adminBookingService.transformBookingData(booking)
      );
      console.log('Transformed bookings:', transformedBookings);
      
      setBookings(transformedBookings);
    } catch (err) {
      console.error('Error loading bookings:', err);
      if (err.message.includes('Authentication failed') || err.message.includes('No authentication token')) {
        setError('Authentication failed. Please login again.');
      } else {
        setError('Failed to load bookings. Please try again.');
      }
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  // Load pending bookings function
  const loadPendingBookings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Check if user is authenticated
      if (!validateToken()) {
        throw new Error('Authentication failed. Please login again.');
      }
      
      console.log('Loading pending bookings...');
      const apiBookings = await adminBookingService.fetchPendingBookings();
      console.log('Pending API bookings received:', apiBookings);
      
      const transformedBookings = apiBookings.map(booking => 
        adminBookingService.transformBookingData(booking)
      );
      console.log('Transformed pending bookings:', transformedBookings);
      
      setBookings(transformedBookings);
    } catch (err) {
      console.error('Error loading pending bookings:', err);
      if (err.message.includes('Authentication failed') || err.message.includes('No authentication token')) {
        setError('Authentication failed. Please login again.');
      } else {
        setError('Failed to load pending bookings. Please try again.');
      }
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  // Load confirmed bookings function
  const loadConfirmedBookings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Check if user is authenticated
      if (!validateToken()) {
        throw new Error('Authentication failed. Please login again.');
      }
      
      console.log('Loading confirmed bookings...');
      const apiBookings = await adminBookingService.fetchConfirmedBookings();
      console.log('Confirmed API bookings received:', apiBookings);
      
      const transformedBookings = apiBookings.map(booking => 
        adminBookingService.transformBookingData(booking)
      );
      console.log('Transformed confirmed bookings:', transformedBookings);
      
      setBookings(transformedBookings);
    } catch (err) {
      console.error('Error loading confirmed bookings:', err);
      if (err.message.includes('Authentication failed') || err.message.includes('No authentication token')) {
        setError('Authentication failed. Please login again.');
      } else {
        setError('Failed to load confirmed bookings. Please try again.');
      }
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  // Load completed bookings function
  const loadCompletedBookings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Check if user is authenticated
      if (!validateToken()) {
        throw new Error('Authentication failed. Please login again.');
      }
      
      console.log('Loading completed bookings...');
      const apiBookings = await adminBookingService.fetchCompletedBookings();
      console.log('Completed API bookings received:', apiBookings);
      
      const transformedBookings = apiBookings.map(booking => 
        adminBookingService.transformBookingData(booking)
      );
      console.log('Transformed completed bookings:', transformedBookings);
      
      setBookings(transformedBookings);
    } catch (err) {
      console.error('Error loading completed bookings:', err);
      if (err.message.includes('Authentication failed') || err.message.includes('No authentication token')) {
        setError('Authentication failed. Please login again.');
      } else {
        setError('Failed to load completed bookings. Please try again.');
      }
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Check authentication before loading data
    if (!validateToken()) {
      setError('Authentication failed. Redirecting to login...');
      setLoading(false);
      return;
    }
    
    loadAllBookings();
  }, []);

  // Handle search term changes
  useEffect(() => {
    debouncedSearch(searchTerm);
  }, [searchTerm, debouncedSearch]);

  // Handle filter changes
  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setSearchTerm(''); // Clear search when changing filters
    
    if (newFilter === 'pending') {
      loadPendingBookings();
    } else if (newFilter === 'confirmed') {
      loadConfirmedBookings();
    } else if (newFilter === 'completed') {
      loadCompletedBookings();
    } else {
      loadAllBookings();
    }
  };

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      await adminBookingService.updateBookingStatus(bookingId, newStatus);
      // Reload bookings after status update based on current filter
      if (filter === 'pending') {
        const apiBookings = await adminBookingService.fetchPendingBookings();
        const transformedBookings = apiBookings.map(booking => 
          adminBookingService.transformBookingData(booking)
        );
        setBookings(transformedBookings);
      } else if (filter === 'confirmed') {
        const apiBookings = await adminBookingService.fetchConfirmedBookings();
        const transformedBookings = apiBookings.map(booking => 
          adminBookingService.transformBookingData(booking)
        );
        setBookings(transformedBookings);
      } else if (filter === 'completed') {
        const apiBookings = await adminBookingService.fetchCompletedBookings();
        const transformedBookings = apiBookings.map(booking => 
          adminBookingService.transformBookingData(booking)
        );
        setBookings(transformedBookings);
      } else {
        const apiBookings = await adminBookingService.fetchAllBookings();
        const transformedBookings = apiBookings.map(booking => 
          adminBookingService.transformBookingData(booking)
        );
        setBookings(transformedBookings);
      }
    } catch (err) {
      console.error('Error updating booking status:', err);
      setError('Failed to update booking status. Please try again.');
    }
  };

  const confirmBooking = async (booking) => {
    try {
      setConfirmingBooking(booking.id);
      console.log('Confirming booking:', booking);
      
      // Prepare booking data for confirmation API
      const bookingData = {
        id: booking.id,
        name: booking.name,
        mobile: booking.mobile,
        address: booking.address,
        landmark: booking.landmark,
        city: booking.city,
        pincode: booking.pincode,
        scrapType: booking.scrapType,
        quantity: booking.quantity,
        photoUrl: booking.photoUrl,
        notes: booking.notes,
        bookingId: booking.bookingId,
        status: "Confirmed", // Set status to Confirmed
        pickupDate: booking.pickupDate,
        pickupSlot: booking.pickupSlot,
        createdAt: booking.createdAt,
        updatedAt: new Date().toISOString(),
        stepStatus: booking.stepStatus
      };

      await adminBookingService.confirmBooking(bookingData);
      
      // Reload bookings after confirmation based on current filter
      if (filter === 'pending') {
        const apiBookings = await adminBookingService.fetchPendingBookings();
        const transformedBookings = apiBookings.map(booking => 
          adminBookingService.transformBookingData(booking)
        );
        setBookings(transformedBookings);
      } else if (filter === 'confirmed') {
        const apiBookings = await adminBookingService.fetchConfirmedBookings();
        const transformedBookings = apiBookings.map(booking => 
          adminBookingService.transformBookingData(booking)
        );
        setBookings(transformedBookings);
      } else if (filter === 'completed') {
        const apiBookings = await adminBookingService.fetchCompletedBookings();
        const transformedBookings = apiBookings.map(booking => 
          adminBookingService.transformBookingData(booking)
        );
        setBookings(transformedBookings);
      } else {
        const apiBookings = await adminBookingService.fetchAllBookings();
        const transformedBookings = apiBookings.map(booking => 
          adminBookingService.transformBookingData(booking)
        );
        setBookings(transformedBookings);
      }
      
      console.log('Booking confirmed successfully');
    } catch (err) {
      console.error('Error confirming booking:', err);
      if (err.message.includes('Authentication failed')) {
        setError('Authentication failed. Please login again.');
      } else {
        setError('Failed to confirm booking. Please try again.');
      }
    } finally {
      setConfirmingBooking(null);
    }
  };

  // Apply status filter to the bookings (search results are already filtered by search term)
  const filteredBookings = bookings.filter(booking => {
    return filter === 'all' || booking.status === filter;
  });

  // Sorting logic
  const statusRank = {
    'pending': 1,
    'confirmed': 2,
    'in-progress': 3,
    'completed': 4,
    'cancelled': 5,
  };

  const sortedBookings = [...filteredBookings].sort((a, b) => {
    let comparison = 0;
    if (sortBy === 'date') {
      const dateA = new Date(a.createdAt || a.updatedAt || a.preferredDate || 0).getTime();
      const dateB = new Date(b.createdAt || b.updatedAt || b.preferredDate || 0).getTime();
      comparison = dateA - dateB;
    } else if (sortBy === 'status') {
      const rankA = statusRank[a.status] ?? 999;
      const rankB = statusRank[b.status] ?? 999;
      comparison = rankA - rankB;
    }
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  // Get search type indicator (matches backend logic)
  const getSearchType = (term) => {
    if (!term) return '';
    if (term.startsWith('Bk') || term.startsWith('BK')) {
      return 'Booking ID';
    }
    if (/^\d+$/.test(term)) {
      return 'Mobile';
    }
    return 'Name';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#f39c12';
      case 'confirmed': return '#3498db';
      case 'in-progress': return '#9b59b6';
      case 'completed': return '#27ae60';
      case 'cancelled': return '#e74c3c';
      default: return '#95a5a6';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading bookings...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-dashboard">
        <div className="error-container">
          <h2>Error</h2>
          <p>{error}</p>
          <div className="error-actions">
            <button onClick={() => window.location.reload()}>Retry</button>
            {error.includes('Access denied') && (
              <button 
                onClick={() => {
                  localStorage.removeItem('token');
                  localStorage.removeItem('role');
                  window.location.href = '/login';
                }}
                className="logout-btn"
              >
                Login Again
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <div className="header-top">
          <h1>Admin Dashboard</h1>
          <div className="header-nav">
            <Link to="/dashboard" className="nav-link active">
              <BarChart3 size={20} />
              Bookings
            </Link>
            <Link to="/prices" className="nav-link">
              <Settings size={20} />
              Price Management
            </Link>
          </div>
        </div>
        <div className="dashboard-stats">
          <div className="stat-card">
            <h3>Total Bookings</h3>
            <p>{bookings.length}</p>
          </div>
          <div className="stat-card">
            <h3>Pending</h3>
            <p>{bookings.filter(b => b.status === 'pending').length}</p>
          </div>
          <div className="stat-card">
            <h3>Completed</h3>
            <p>{bookings.filter(b => b.status === 'completed').length}</p>
          </div>
        </div>
      </div>

      <div className="dashboard-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by name, mobile, or booking ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            disabled={searchLoading}
          />
          {searchLoading && (
            <div className="search-loading">
              <div className="mini-spinner"></div>
            </div>
          )}
          {searchTerm && !searchLoading && (
            <button 
              className="clear-search-btn"
              onClick={() => setSearchTerm('')}
              title="Clear search"
            >
              ×
            </button>
          )}
        </div>
        <div className="filter-buttons">
          <button 
            className={filter === 'all' ? 'active' : ''}
            onClick={() => handleFilterChange('all')}
          >
            All
          </button>
          <button 
            className={filter === 'pending' ? 'active' : ''}
            onClick={() => handleFilterChange('pending')}
          >
            Pending
          </button>
          <button 
            className={filter === 'confirmed' ? 'active' : ''}
            onClick={() => handleFilterChange('confirmed')}
          >
            Confirmed
          </button>
          <button 
            className={filter === 'completed' ? 'active' : ''}
            onClick={() => handleFilterChange('completed')}
          >
            Completed
          </button>
        </div>
        <div className="sort-controls">
          <label>Sort by:</label>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="date">Date</option>
            <option value="status">Status</option>
          </select>
          <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
            <option value="asc">Asc</option>
            <option value="desc">Desc</option>
          </select>
        </div>
      </div>

      {/* Filter and Search Results Indicator */}
      {(filter !== 'all' || searchTerm) && (
        <div className="search-indicator">
          <span className="search-info">
            {filter !== 'all' && (
              <>
                Showing <strong>{filter === 'pending' ? 'Pending' : filter.charAt(0).toUpperCase() + filter.slice(1)}</strong> bookings
                {filter === 'pending' && <span className="api-indicator"> (via /bookings/pending API)</span>}
                {searchTerm && <span> • Searching by <strong>{getSearchType(searchTerm)}</strong>: "{searchTerm}"</span>}
              </>
            )}
            {filter === 'all' && searchTerm && (
              <>
                Searching by <strong>{getSearchType(searchTerm)}</strong>: "{searchTerm}"
              </>
            )}
            {searchLoading && <span className="search-loading-text"> (Searching...)</span>}
          </span>
          <span className="results-count">
            {sortedBookings.length} result{sortedBookings.length !== 1 ? 's' : ''} found
          </span>
        </div>
      )}

      <div className="bookings-list">
        {sortedBookings.length === 0 ? (
          <div className="no-bookings">
            <p>No bookings found</p>
          </div>
        ) : (
          sortedBookings.map(booking => (
            <div key={booking.bookingId} className="booking-card">
              <div className="booking-header">
                <div className="booking-info">
                  <h3>{booking.name}</h3>
                  <p>ID: {booking.bookingId}</p>
                  <p>Mobile: {booking.mobile}</p>
                </div>
                <div className="booking-status">
                  <span 
                    className="status-badge"
                    style={{ backgroundColor: getStatusColor(booking.status) }}
                  >
                    {booking.status.toUpperCase()}
                  </span>
                </div>
              </div>
              
              <div className="booking-details">
                <div className="detail-row">
                  <strong>Address:</strong> {booking.address}, {booking.city} - {booking.pincode}
                </div>
                <div className="detail-row">
                  <strong>Scrap Type:</strong> {booking.scrapType} | <strong>Quantity:</strong> {booking.quantity}
                </div>
                <div className="detail-row">
                  <strong>Preferred Date:</strong> {booking.preferredDate} | <strong>Time:</strong> {booking.preferredTime}
                </div>
                <div className="detail-row">
                  <strong>Photos:</strong> {booking.photos.length} uploaded
                </div>
                <div className="detail-row">
                  <strong>Created:</strong> {formatDate(booking.createdAt)}
                </div>
              </div>

              <div className="booking-actions">
                <button 
                  className="view-details-btn"
                  onClick={() => setSelectedBooking(booking)}
                >
                  View Details
                </button>
                <div className="status-actions">
                  {booking.status === 'pending' && (
                    <>
                      <button 
                        className="confirm-btn"
                        onClick={() => confirmBooking(booking)}
                        disabled={confirmingBooking === booking.id}
                      >
                        {confirmingBooking === booking.id ? 'Confirming...' : 'Confirm'}
                      </button>
                      <button 
                        className="cancel-btn"
                        onClick={() => updateBookingStatus(booking.id, 'cancelled')}
                      >
                        Cancel
                      </button>
                    </>
                  )}
                  {booking.status === 'confirmed' && (
                    <button 
                      className="start-btn"
                      onClick={() => updateBookingStatus(booking.id, 'in-progress')}
                    >
                      Start Pickup
                    </button>
                  )}
                  {booking.status === 'in-progress' && (
                    <button 
                      className="complete-btn"
                      onClick={() => updateBookingStatus(booking.id, 'completed')}
                    >
                      Complete
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {selectedBooking && (
        <div className="modal-overlay" onClick={() => setSelectedBooking(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Booking Details - {selectedBooking.bookingId}</h2>
              <button 
                className="close-btn"
                onClick={() => setSelectedBooking(null)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="detail-section">
                <h3>Customer Information</h3>
                <p><strong>Name:</strong> {selectedBooking.name}</p>
                <p><strong>Mobile:</strong> {selectedBooking.mobile}</p>
                <p><strong>Address:</strong> {selectedBooking.address}</p>
                <p><strong>City:</strong> {selectedBooking.city}</p>
                <p><strong>Pincode:</strong> {selectedBooking.pincode}</p>
                {selectedBooking.landmark && (
                  <p><strong>Landmark:</strong> {selectedBooking.landmark}</p>
                )}
              </div>

              <div className="detail-section">
                <h3>Scrap Details</h3>
                <p><strong>Type:</strong> {selectedBooking.scrapType}</p>
                <p><strong>Quantity:</strong> {selectedBooking.quantity}</p>
                {selectedBooking.additionalNotes && (
                  <p><strong>Notes:</strong> {selectedBooking.additionalNotes}</p>
                )}
              </div>

              <div className="detail-section">
                <h3>Schedule</h3>
                <p><strong>Preferred Date:</strong> {selectedBooking.preferredDate}</p>
                <p><strong>Preferred Time:</strong> {selectedBooking.preferredTime}</p>
              </div>

              {selectedBooking.photos.length > 0 && (
                <div className="detail-section">
                  <h3>Photos</h3>
                  <div className="photos-grid">
                    {selectedBooking.photos.map((photo, index) => (
                      <div key={index} className="photo-item">
                        <img src={photo.preview} alt={`Scrap item ${index + 1}`} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
